// CalendarDocuments.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import itLocale from "@fullcalendar/core/locales/it";
import dayjs from "dayjs";
import "dayjs/locale/it";
import Swal from "sweetalert2";
import Fetching from "./fetching";
import LoadingState from "./fetchingV2";

import EditDocumentModal from "./edit_document_modal";
import EditContractModal from "./edit_contract_modal";
import { fetchDoc, updateDocumentV2, updateSignedDocumentDeadline } from "../services/api";
import { updateContractEndDate } from "../services/api_contracts_employee";

import { UserMinusIcon, DocumentTextIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import CustomTooltipComponent from "./custom_tooltip_component";
import AttendanceControlsBar from "./attendance_controls_bar";
import QuarterlyView from "./quarterly_view";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import ModalExtractionDeadlines from "./modal_extraction_deadlines";
import SchedulerContextBar from "./scheduler_context_bar";

// Palette
const DOC_COLOR = { bg: "#e0e7ff", border: "#6366f1", text: "#111827" }; // indigo
const CTR_COLOR = { bg: "#fef3c7", border: "#f59e0b", text: "#111827" }; // amber
const SIGNED_DOC_COLOR = { bg: "#dcfce7", border: "#16a34a", text: "#14532d" };

// INFO 
// Se azienda ha il piano Base viene bloccata la chiamata di get Unità Organizzative 
// e non viene mostrato il componente scheduler control bar che consente il filtro delle ou


export default function CalendarDocuments({
  token,
  profile,
  docs = [],
  signedDocs = [],
  contracts = [],
  onDocUpdated,
  onSignedDocUpdated,
  onContractUpdated,
  onDatesSet,
  isLoading = false,
  initialDate,
  units = [],
  selectedUnit,
  onSelectUnit,
  onResetUnit,
  isUnitsLoading = false,
}) {
  const calRef = useRef(null);

  const [viewDoc, setViewDoc] = useState(null);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [viewContract, setViewContract] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState("document");

  // filtri locali
  const [showDocs, setShowDocs] = useState(true);
  const [showContracts, setShowContracts] = useState(true);
  const [showSignedDocs, setShowSignedDocs] = useState(true);

  const [mode, setMode] = useState("MENSILE"); // "GIORNALIERO" | "SETTIMANALE" | "MENSILE" | "TRIMESTRALE"
  const [controlDate, setControlDate] = useState(new Date());
  const isQuarterView = mode === "TRIMESTRALE";

  const safeDocs = useMemo(() => Array.isArray(docs) ? docs : [], [docs]);
  const safeContracts = useMemo(() => Array.isArray(contracts) ? contracts : [], [contracts]);
  const safeSignedDocs = useMemo(() => Array.isArray(signedDocs) ? signedDocs : [], [signedDocs]);

  const isEmployee = profile.customer_type === 'employee';
  const hasViewDocsPermission =
    !isEmployee ||
    (
      isEmployee &&
      (
        profile.employee?.permissions?.includes('hr.view_docs_ou') ||
        profile.employee?.permissions?.includes('hr.view_docs_all')
      )
    );

  // mapping vista <-> bar
  const modeToView = {
    GIORNALIERO: "dayGridDay",
    SETTIMANALE: "dayGridWeek",
    MENSILE: "dayGridMonth",
    TRIMESTRALE: "dayGridThreeMonth",
  };

  const viewToMode = {
    dayGridDay: "GIORNALIERO",
    dayGridWeek: "SETTIMANALE",
    dayGridMonth: "MENSILE",
    dayGridThreeMonth: "TRIMESTRALE",
  };

  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [showFilters, setShowFilters] = useState(true); 
  const [filterSelected, setFilterSelected] = useState('');
  const [calendarRange, setCalendarRange] = useState(null);

  // helper inizio periodo (lunedì per settimana)
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const startOfWeekMonday = (d) => {
    const nd = new Date(d);
    const dow = nd.getDay(); // 0=dom … 6=sab
    const diff = dow === 0 ? -6 : 1 - dow;
    nd.setDate(nd.getDate() + diff);
    return nd;
  };

  // ---- EMISSIONE RANGE NORMALIZZATO AL PARENT ----
  const emitRangeFromCalendar = useCallback(() => {
    const api = calRef.current?.getApi();
    if (!api) return;

    const isMonthView =
      api.view.type === "dayGridMonth" || api.view.type === "dayGridThreeMonth";

    const start = dayjs(isMonthView ? api.view.currentStart : api.view.activeStart);
    const endInc = dayjs(
      isMonthView ? api.view.currentEnd : api.view.activeEnd
    ).subtract(1, "day");

    const dateFrom = start.format("YYYY-MM-DD");
    const dateTo = endInc.format("YYYY-MM-DD");

    const startStr = start.toISOString();
    const endStr = endInc.endOf("day").toISOString();

    onDatesSet?.({
      dateFrom,
      dateTo,
      startStr,
      endStr,
      viewType: api.view.type,
    });
  }, [onDatesSet]);

  // ---- GESTIONE initialDate (es: da ?month=2025-03-01) ----
  useEffect(() => {
    if (!initialDate) return;

    const api = calRef.current?.getApi();
    const target = new Date(initialDate);

    setControlDate(target);

    if (!api) {
      // se l'API non è ancora pronta, al primo datesSet
      // verrà comunque emesso il range corretto
      return;
    }

    api.changeView("dayGridMonth");
    api.gotoDate(target);
    requestAnimationFrame(emitRangeFromCalendar);
  }, [initialDate, emitRangeFromCalendar]);

  // ---- HANDLER: CAMBIO VISTA DAL BAR ----
  const handleModeChange = (newMode) => {
    setMode(newMode);
    const api = calRef.current?.getApi();
    if (!api) return;

    const targetDate =
      newMode === "MENSILE"
        ? startOfMonth(controlDate)
        : newMode === "SETTIMANALE"
        ? startOfWeekMonday(controlDate)
        : newMode === "TRIMESTRALE"
        ? new Date(controlDate.getFullYear(), controlDate.getMonth(), 1)
        : controlDate;

    api.changeView(modeToView[newMode]);
    api.gotoDate(targetDate);

    requestAnimationFrame(emitRangeFromCalendar);
  };

  // ---- HANDLER: CAMBIO DATA DAL BAR (frecce / picker custom) ----
  const handleDateChange = (newDate) => {
    setControlDate(newDate);
    const api = calRef.current?.getApi();
    if (!api) return;

    api.gotoDate(newDate);
    requestAnimationFrame(emitRangeFromCalendar);
  };

  // ---- SYNC calendario -> bar quando cambia la view ----
  const _onDatesSet = useCallback(
    (arg) => {
      const newMode = viewToMode[arg.view.type] || "MENSILE";
      setMode(newMode);

      // currentStart è l'inizio del range della view (per 3 mesi = primo mese)
      const base = new Date(arg.view.currentStart);

      const barDate =
        newMode === "MENSILE"
          ? startOfMonth(base)
          : newMode === "SETTIMANALE"
          ? startOfWeekMonday(base)
          : newMode === "TRIMESTRALE"
          ? new Date(base.getFullYear(), base.getMonth(), 1)
          : base;

      setControlDate(barDate);

      const isMonthView =
        arg.view.type === "dayGridMonth" || arg.view.type === "dayGridThreeMonth";

      const start = dayjs(isMonthView ? arg.view.currentStart : arg.view.activeStart);
      const endInc = dayjs(
        isMonthView ? arg.view.currentEnd : arg.view.activeEnd
      ).subtract(1, "day");

      const nextRange = {
        dateFrom: start.format("YYYY-MM-DD"),
        dateTo: endInc.format("YYYY-MM-DD"),
        startStr: start.toISOString(),
        endStr: endInc.endOf("day").toISOString(),
        viewType: arg.view.type,
      };

      setCalendarRange(nextRange);

      onDatesSet?.(nextRange);
    },
    [onDatesSet]
  );

  // ---- eventi (docs + contratti) ----
  const getContractDueDate = (c) => c.due_date || c.end_date || null;

  const buildContractTitle = (c) => {
    const emp = c.employee || {};
    if (emp.last_name || emp.first_name) {
      const ln = (emp.last_name || "").trim();
      const fn = (emp.first_name || "").trim();
      const full = [ln, fn].filter(Boolean).join(" ").trim();
      return `${full}`;
    }
    const cf = (emp.cf || "").trim();
    return cf;
  };

  const events = useMemo(() => {
    const docEvents = (showDocs ? safeDocs : [])
      .filter((d) => !!d.due_date)
      .map((d) => ({
        id: `doc-${d.id}`,
        title: (d.name ?? d.nome ?? d.title ?? `Documento #${d.id}`).toUpperCase(),
        start: dayjs(d.due_date).format("YYYY-MM-DD"),
        allDay: true,
        backgroundColor: DOC_COLOR.bg,
        borderColor: DOC_COLOR.border,
        textColor: DOC_COLOR.text,
        extendedProps: { kind: "document", entityId: Number(d.id) },
      }));


    // DOCUMENTI CON FIRMA
    const signedDocEvents = (showSignedDocs ? safeSignedDocs : [])
      .filter((d) => !!d.due_date)
      .map((d) => ({
        id: `signed-doc-${d.id}`,
        title: (d.name ?? `Documento con firma #${d.id}`).toUpperCase(),
        start: dayjs(d.due_date).format("YYYY-MM-DD"),
        allDay: true,
        backgroundColor: SIGNED_DOC_COLOR.bg,
        borderColor: SIGNED_DOC_COLOR.border,
        textColor: SIGNED_DOC_COLOR.text,
        
        startEditable: true,
        durationEditable: false,

        extendedProps: {
          kind: "signed_document",
          entityId: Number(d.id),
          employeeId: d.employee?.id ?? d.employee_id ?? null,
          raw: d,
        },
      }));


    // CONTRATTI
    const ctrEvents = (showContracts ? safeContracts : [])
      .map((c) => {
        const due = getContractDueDate(c);
        if (!due) return null;
        return {
          id: `ctr-${c.id}`,
          title: buildContractTitle(c).toUpperCase(),
          start: dayjs(due).format("YYYY-MM-DD"),
          allDay: true,
          backgroundColor: CTR_COLOR.bg,
          borderColor: CTR_COLOR.border,
          textColor: CTR_COLOR.text,
          extendedProps: {
            kind: "contract",
            entityId: Number(c.id),
            employeeId: c.employee?.id ?? c.employee_id ?? null,
            startDate: c.start_date || null,
            raw: { ...c, due_date: due },
          },
        };
      })
      .filter(Boolean);

    return [...docEvents, ...signedDocEvents, ...ctrEvents];
  }, [safeDocs, safeSignedDocs, safeContracts, showDocs, showSignedDocs, showContracts]);

  // Flag per giorno (icone in cella)
  const dayFlags = useMemo(() => {
    const out = {};
    (events || []).forEach((ev) => {
      const ymd = dayjs(ev.start).format("YYYY-MM-DD");
      out[ymd] ||= { doc: false, signedDoc: false, ctr: false };
      if (ev.extendedProps?.kind === "document") out[ymd].doc = true;
      if (ev.extendedProps?.kind === "signed_document") out[ymd].signedDoc = true;
      if (ev.extendedProps?.kind === "contract") out[ymd].ctr = true;
    });
    return out;
  }, [events]);

  // cella giorno (icone + numero)
  const renderDayCell = useCallback(
    (arg) => {
      const ymd = dayjs(arg.date).format("YYYY-MM-DD");
      const flags = dayFlags[ymd] || { doc: false, signedDoc: false, ctr: false };

      const isQuarterViewLocal = mode === "TRIMESTRALE";
      const isFirstOfMonth = dayjs(arg.date).date() === 1;

      const numberBaseClasses = "fc-daygrid-day-number ml-auto w-full flex justify-end";

      const numberStyle =
        isQuarterViewLocal && isFirstOfMonth
          ? { fontWeight: 600, textTransform: "uppercase", color: "#6d28d9" }
          : { fontWeight: 500, color: "#374151" };

      return (
        <div className="flex w-full items-center justify-between px-1">
          <span className="inline-flex items-center gap-1">
            {flags.doc && (
              <CustomTooltipComponent title="Documenti in scadenza">
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                  style={{ backgroundColor: DOC_COLOR.bg, color: DOC_COLOR.border }}
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" />
                </span>
              </CustomTooltipComponent>
            )}
            {flags.signedDoc && (
              <CustomTooltipComponent title="Documenti con firma in scadenza">
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: SIGNED_DOC_COLOR.bg,
                    color: SIGNED_DOC_COLOR.border,
                  }}
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                </span>
              </CustomTooltipComponent>
            )}
            {flags.ctr && (
              <CustomTooltipComponent title="Contratti in scadenza">
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                  style={{ backgroundColor: CTR_COLOR.bg, color: CTR_COLOR.border }}
                >
                  <UserMinusIcon className="h-3.5 w-3.5" />
                </span>
              </CustomTooltipComponent>
            )}
          </span>

          <span className={numberBaseClasses} style={numberStyle}>
            {arg.dayNumberText}
          </span>
        </div>
      );
    },
    [dayFlags, mode]
  );

  // contatori: mese singolo oppure trimestre (in base alla view)
  const monthCounters = useMemo(() => {
    let rangeStart;
    let rangeEnd;

    if (mode === "TRIMESTRALE") {
      // controlDate è il PRIMO mese del range trimestrale
      const firstMonth = dayjs(controlDate).startOf("month");
      const thirdMonthEnd = firstMonth.add(2, "month").endOf("month");
      rangeStart = firstMonth;
      rangeEnd = thirdMonthEnd;
    } else {
      // viste normali: solo il mese di controlDate
      rangeStart = dayjs(controlDate).startOf("month");
      rangeEnd = dayjs(controlDate).endOf("month");
    }

    const inRange = (d) => !!d && dayjs(d).isBetween(rangeStart, rangeEnd, "day", "[]");

    const docsCount = safeDocs.reduce((acc, d) => acc + (inRange(d.due_date) ? 1 : 0), 0);
    const signedDocsCount = safeSignedDocs.reduce((acc, d) => acc + (inRange(d.due_date) ? 1 : 0), 0);
    const ctrsCount = safeContracts.reduce((acc, c) => {
      const due = c.due_date || c.end_date;
      return acc + (inRange(due) ? 1 : 0);
    }, 0);

    return { docs: docsCount, signedDocs: signedDocsCount, ctrs: ctrsCount };
  }, [safeDocs, safeSignedDocs, safeContracts, controlDate, mode]);

  // rendering evento
  const renderEventContent = useCallback((arg) => {
  const kind = arg.event.extendedProps?.kind;

  const Icon = kind === "contract" ? UserMinusIcon : kind === "signed_document"
        ? PencilSquareIcon
        : DocumentTextIcon;
    const fullTitle = arg.event.title || "";
    const shortTitle = fullTitle.length > 40 ? `${fullTitle.slice(0, 40)}…` : fullTitle;

    return (
      <div
        className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold w-full overflow-hidden"
        title={fullTitle}
      >
        <Icon className="h-3.5 w-3.5 flex-none" />
        <span className="flex-1 min-w-0 truncate leading-4">{shortTitle}</span>
      </div>
    );
  }, []);

  // ---- FUNZIONE UNICA DI UPDATE: usata da FullCalendar + QuarterlyView ----
  const processDrop = useCallback(
    async ({ kind, entityId, employeeId, startDate, endYMD, revert }) => {
      // Validazioni preliminari
      if (kind === "contract") {
        if (!employeeId) {
          revert?.();
          Swal.fire({ icon: "error", title: "Dati mancanti", text: "Id dipendente non presente." });
          return;
        }
        if (startDate && endYMD < startDate) {
          revert?.();
          Swal.fire({
            icon: "warning",
            title: "Data non valida",
            text: "La data fine non può essere precedente alla data inizio contratto.",
          });
          return;
        }
      }
      // Verifica tipologia supportata
      if (!["contract", "document", "signed_document"].includes(kind)) {
        revert?.();
        return;
      }
      // Loader durante aggiornamento
      Swal.fire({
        title: "Aggiornamento scadenza...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        if (kind === "contract") {
          await updateContractEndDate(token, employeeId, entityId, {
              start_date: startDate,
              end_date: endYMD,
            }
          );

          await onContractUpdated?.({ id: entityId, start_date: startDate, end_date: endYMD });
        }

        else if (kind === "document") {
          const updated = await updateDocumentV2(token, entityId, {due_date: `${endYMD}T00:00:00`,});

          await onDocUpdated?.({ ...updated, id: entityId, due_date: `${endYMD}T00:00:00`,});
        }

        else if (kind === "signed_document") {
          const updated = await updateSignedDocumentDeadline(token, entityId, {due_date: `${endYMD}T00:00:00`}, "payrolls");

          await onSignedDocUpdated?.({ ...updated, id: entityId, due_date: `${endYMD}T00:00:00`});
        }

        // Chiude il loader e mostra successo
        Swal.fire({
          icon: "success",
          title: "Scadenza aggiornata",
          timer: 1200,
          showConfirmButton: false,
        });

      } catch (err) {
        // Ripristina la posizione originale in caso di errore
        revert?.();
        const msg = err?.response?.data
          ? Object.values(err.response.data).flat().join(" ")
          : err?.message || "Errore durante l'aggiornamento.";

        Swal.fire({
          icon: "error",
          title: "Aggiornamento non riuscito",
          text: msg,
          confirmButtonText: "OK",
        });
      }
    },
    [token, onDocUpdated, onSignedDocUpdated, onContractUpdated]
  );

  // drag & drop (FullCalendar)
  const handleEventDrop = useCallback(
    async (info) => {
      const { kind, entityId, employeeId, startDate } = info.event.extendedProps;
      const endYMD = info.event.startStr.slice(0, 10);

      await processDrop({
        kind,
        entityId,
        employeeId,
        startDate,
        endYMD,
        revert: () => info.revert(),
      });
    },
    [processDrop]
  );

  // OPEN ITEM (document / signed_document / contract)
  const openItem = useCallback(
    async ({ kind, id, entityId, raw, employeeId }) => {

      const documentId = id ?? entityId;

      // CONTRATTI
      if (kind === "contract") {
        const c = raw || {};

        const empId = employeeId ?? c.employee?.id ?? c.employee_id ?? null;

        setViewContract({
          ...c,
          id: documentId,
          employeeId: empId,
        });

        return;
      }

      // DOCUMENTI CON FIRMA
      if (kind === "signed_document") {
        const signedDoc =
          safeSignedDocs.find(d => Number(d.id) === Number(documentId)) ||
          raw;

        if (!signedDoc || documentId == null) {
          Swal.fire({
            icon: "error",
            title: "Documento non trovato",
            text: "Impossibile recuperare il documento con firma.",
          });
          return;
        }

        setSelectedDocType("signed_document");

        setViewDoc({
          ...signedDoc,
          id: documentId,
        });

        setOpenDocModal(true);

        return;
      }

      // DOCUMENTI ORDINARI
      if (kind === "document") {
        setLoadingDoc(true);

        try {
          const full = await fetchDoc(
            token,
            documentId,
            "payrolls"
          );

          setSelectedDocType("document");
          setViewDoc(full);
          setOpenDocModal(true);

        } catch (err) {
          const msg = err?.response?.data
            ? Object.values(err.response.data).flat().join(" ")
            : err?.message || "Errore caricamento documento.";

          Swal.fire({
            icon: "error",
            title: "Impossibile aprire il documento",
            text: msg,
          });
        } finally {
          setLoadingDoc(false);
        }
      }
    },
    [token, safeSignedDocs]
  );

  // click evento → modali (FullCalendar)
  const handleEventClick = useCallback(
    async (info) => {
      const kind = info.event.extendedProps.kind;
      const id = info.event.extendedProps.entityId;
      const raw = info.event.extendedProps.raw || {};
      const employeeId =
        info.event.extendedProps.employeeId ?? raw.employee?.id ?? raw.employee_id ?? null;

      openItem({ kind, id, raw, employeeId });
    },
    [openItem]
  );

  // evidenziazione cella
  const handleDayCellDidMount = useCallback((arg) => {
    if (arg.isToday) {
      arg.el.style.backgroundColor = "rgba(199, 210, 254, 0.2)";
      arg.el.style.boxShadow = "inset 0 0 0 1px #c7d2fe";
    }
    if (arg.isOther) {
      arg.el.style.backgroundColor = "#fff";
      const bg = arg.el.querySelector(".fc-daygrid-day-bg");
      if (bg) bg.style.backgroundColor = "#fff";
    }
  }, []);

  const handleDayHeaderDidMount = useCallback((arg) => {
    const a = arg.el.querySelector(".fc-col-header-cell-cushion");
    if (a) {
      a.style.display = "block";
      a.style.width = "100%";
      a.style.textAlign = "center";
      a.style.textTransform = "uppercase";
      a.style.letterSpacing = "0.03em";
      a.style.fontWeight = "500";
      a.style.color = "#6d28d9";
      a.style.backgroundColor = "#eef2ff";
      a.style.padding = "0.5rem 0";
      a.style.fontSize = "14px";
    }
  }, []);

  // mostra tutto nel giornaliero, 5 nelle altre viste
  const dayMaxForView = mode === "GIORNALIERO" ? false : 5;

  const handleSelectFilters = () => {
    setShowModal(true);
    setFilterSelected(filter);
  };

  // overlay loading documento
  const LoadingOverlay = ({ show }) =>
    show ? (
      <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center rounded-md" style={{ zIndex: 900 }}>
        <div className=" text-sm font-medium text-indigo-800  px-3 py-1 shadow-sm w-full">
          <Fetching />
        </div>
      </div>
    ) : null;
  
  const modalDefaultDates = useMemo(() => {
    if (calendarRange?.dateFrom && calendarRange?.dateTo) {
      return {
        startDate: calendarRange.dateFrom,
        endDate: calendarRange.dateTo,
      };
    }

    const base = dayjs(controlDate);

    return {
      startDate: base.startOf("month").format("YYYY-MM-DD"),
      endDate: base.endOf("month").format("YYYY-MM-DD"),
    };
  }, [calendarRange, controlDate]);

  const hasUnits = Array.isArray(units) && units.length > 0;

  return (
    <div className="relative bg-white space-y-4">
      {isUnitsLoading ? (
        <LoadingState
          spinnerSize={30}
          label="Caricamento..."
          center={true}
          fullWidth={true}
          minHeight="auto"
          className="inline-block align-middle border border-dashed rounded-md border-gray-300 py-2 italic"
        />
      ) : hasUnits ? (
        <SchedulerContextBar
          units={units}
          selectedUnit={selectedUnit}
          onSelectUnit={onSelectUnit}
          onResetUnit={onResetUnit}
          isUnitsLoading={false}
          token={token}
          contractStartDate={modalDefaultDates.startDate}
          contractEndDate={modalDefaultDates.endDate}
          showEmployees={false}
          showPageSize={false}
          showSelectedEmployeesCount={false}
          stickyClassName="relative"
        />
      ) : null}

      <div className="flex justify-between">
        <div className="justify-start">
          <button
            onClick={handleSelectFilters}
            className="text-white text-xs py-1.5 px-2 rounded uppercase font-bold bg-indigo-600 hover:bg-indigo-500"
          >
            Esporta scadenze
          </button>
        </div>
        {/* filtri + contatori */}
        <div className="flex items-center gap-3 justify-end mb-3">
          <div className="flex items-center gap-2">
            {hasViewDocsPermission && (
              <button
                onClick={() => setShowDocs((v) => !v)}
                className={`text-xs px-2.5 h-8 rounded-full border ${
                  showDocs
                    ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                    : "bg-white border-gray-200 text-gray-600"
                }`}
              >
                DOCUMENTI
                <span
                  className="ml-2 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-[11px] font-semibold"
                  style={{ backgroundColor: DOC_COLOR.bg, color: DOC_COLOR.border }}
                >
                  {monthCounters.docs}
                </span>
              </button>
            )}
            {hasViewDocsPermission && (
              <button
                type="button"
                onClick={() => setShowSignedDocs((v) => !v)}
                className={`text-xs px-2.5 h-8 rounded-full border ${
                  showSignedDocs
                    ? "bg-green-50 border-green-500 text-green-700"
                    : "bg-white border-gray-200 text-gray-600"
                }`}
              >
                DOCUMENTI CON FIRMA

                <span
                  className="ml-2 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-[11px] font-semibold"
                  style={{
                    backgroundColor: SIGNED_DOC_COLOR.bg,
                    color: SIGNED_DOC_COLOR.border,
                  }}
                >
                  {monthCounters.signedDocs}
                </span>
              </button>
            )}
            <button
              onClick={() => setShowContracts((v) => !v)}
              className={`text-xs px-2.5 h-8 rounded-full border ${
                showContracts
                  ? "bg-amber-50 border-amber-500 text-amber-700"
                  : "bg-white border-gray-200 text-gray-600"
              }`}
            >
              CONTRATTI
              <span
                className="ml-2 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-[11px] font-semibold"
                style={{ backgroundColor: CTR_COLOR.bg, color: CTR_COLOR.border }}
              >
                {monthCounters.ctrs}
              </span>
            </button>
          </div>
        </div>
      </div>
      
      <div>
        <AttendanceControlsBar
          mode={mode}
          onModeChange={handleModeChange}
          date={controlDate}
          onDateChange={handleDateChange}
          showQuarterlyTab
        />
      </div>


      {/* Calendario */}
      <div className="relative min-h-[16rem]" style={{ marginTop: '0px'}}>
        {mode !== 'GIORNALIERO' && (
          <div className="border-x border-gray-200 bg-gray-100 text-xs pl-4 pb-2" style={{ fontWeight: '500', }}>Trascina e rilascia una scadenza sul giorno desiderato per aggiornarne la data</div>
        )}
        <div
          style={{ opacity: isLoading ? 0 : 1, transition: "opacity .15s ease" }}
          className={isLoading ? "pointer-events-none select-none" : ""}
        >
          <style>
            {`
              .fc .fc-daygrid-day.fc-day-other,
              .fc .fc-daygrid-day.fc-day-other .fc-daygrid-day-frame,
              .fc .fc-daygrid-day.fc-day-other .fc-daygrid-day-bg,
              .fc .fc-daygrid-day.fc-daygrid-day-top {
                background: #fff !important;
              }
              .fc { --fc-neutral-bg-color: #fff; }
              .fc .fc-popover {
                z-index: 1200 !important;
              }
            `}
          </style>

          {/* FullCalendar sempre montato, nascosto in trimestrale */}
          <div style={{ display: isQuarterView ? "none" : "block" }}>
            <FullCalendar
              ref={calRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale={itLocale}
              height="auto"
              firstDay={1}
              headerToolbar={false}
              events={events}
              dayCellContent={renderDayCell}
              dayCellDidMount={handleDayCellDidMount}
              dayHeaderDidMount={handleDayHeaderDidMount}
              eventContent={renderEventContent}
              eventDidMount={(info) => {
                info.el.style.borderRadius = "8px";
                info.el.style.overflow = "hidden";
                info.el.style.padding = "0";
              }}
              editable
              eventStartEditable
              eventDurationEditable={false}
              eventDrop={handleEventDrop}
              eventClick={handleEventClick}
              datesSet={_onDatesSet}
              dayMaxEvents={dayMaxForView}
              expandRows
              showNonCurrentDates={false}
              fixedWeekCount={false}
              views={{
                dayGridThreeMonth: {
                  type: "dayGrid",
                  duration: { months: 3 },
                },
              }}
            />
          </div>

          {/* Vista trimestrale custom a MESI IN COLONNA */}
          {isQuarterView && (
            <QuarterlyView
              baseDate={controlDate}
              events={events}
              onItemDrop={({
                kind,
                entityId,
                employeeId,
                startDate,
                targetYmd,
              }) =>
                processDrop({
                  kind,
                  entityId,
                  employeeId,
                  startDate,
                  endYMD: targetYmd,
                  revert: null,
                })
              }
              onItemClick={openItem}
            />
          )}
        </div>

        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-top justify-top bg-white w-full">
            <Fetching />
          </div>
        )}

        <LoadingOverlay show={loadingDoc} />
      </div>

      {/* Modali */}
      {viewDoc && (
        <EditDocumentModal
          open={openDocModal}
          onClose={() => setOpenDocModal(false)}
          onReopen={() => setOpenDocModal(true)}
          doc={viewDoc}
          documentType={selectedDocType}
          mode="view"
          allowInlineEditToggle
          showPreviewModal={selectedDocType !== "signed_document"}
          onSaved={(updated) => {
            if (selectedDocType === "signed_document") {
              onSignedDocUpdated?.(updated);
            } else {
              onDocUpdated?.(updated);
            }
            setViewDoc(null); 
            setOpenDocModal(false);
          }}
        />
      )}
      {viewContract && (
        <EditContractModal
          open={!!viewContract}
          onClose={() => setViewContract(null)}
          contract={viewContract}
          onSaved={(updated) => {
            onContractUpdated && onContractUpdated(updated);
            setViewContract(null);
          }}
        />
      )}
      {showModal && (
        <ModalExtractionDeadlines
          show={showModal}
          onClose={() => setShowModal(false)}
          defaultStartDate={modalDefaultDates.startDate}
          defaultEndDate={modalDefaultDates.endDate}
          selectedUnit={selectedUnit}
        />
      )}
    </div>
  );
}